import { Component, Input, Output, OnInit, EventEmitter } from '@angular/core';
import { TrackeritemRest } from '../../services/trackeritem.rest';
import { TrackerService } from '../../services/tracker.service';
import { TrackeritemService } from '../../services/trackeritem.service';
import { UiService } from '../../../../core/services/core.ui.service';
import { GfTranslateService } from '../../../../core/services/core.translate.service';
import { UtilsService } from '../../../../core/services/core.utils.service';
import { UserService } from '../../../../core/services/core.user.service';
import * as _ from 'lodash';

@Component({
    selector: 'trackeritem-multi-update-modal',
    template: require('./trackeritem.multi.update.modal.component.html')
})
export class TrackeritemMultiUpdateModalComponent implements OnInit {
    footerMessage = '';
    @Input() trackeritems: any[];
    @Input() tracker: any;

    @Output() onClose: EventEmitter<any> = new EventEmitter<any>();
    @Output() onUpdate: EventEmitter<any> = new EventEmitter<any>();

    tags: any[] = [];
    errors: any = {};
    isLoaded = false;
    mode = 'multi';
    allowAssignments = true;
    allowUpdate = true;
    updating = false;
    acls = { canAssign: true };
    trackeritem: any;
    assignees: any[];
    workflow: any;
    tagsOriginal: any[];
    extraFieldOptions: any;
    trackeritemOriginal: any;
    updateAssignees: any;
    extraFields: any;

    constructor(
        private trackeritemRest: TrackeritemRest,
        private uiService: UiService,
        private trackerService: TrackerService,
        private trackeritemService: TrackeritemService,
        private translateService: GfTranslateService,
        private utilsService: UtilsService,
        private userService: UserService
    ) { }

    ngOnInit() {
        var _tracker: any = _.cloneDeep(this.tracker);
        const promises: any = [];
        this.assignees = [];

        if (_.has(this.tracker, 'id') && this.tracker.fields.length == 0) {
            promises.push(this.trackerService.load(this.tracker.id));
        } else {
            promises.push(Promise.resolve());
        }

        promises.push(this.trackerService.loadAssignees(this.tracker.id));

        Promise.all(Object.values(promises))
            .then((result: any) => {
                if (!_.isEmpty(result[0])) {
                    _tracker = result[0];
                }

                this.assignees = _.get(result[1], 'items', [this.userService.getNobody()]);
                this.workflow = (_.has(_tracker, 'rel.workflow.fields')) ? _tracker.rel.workflow.fields : {};
                this.tagsOriginal = _.clone(this.tags);
            })
            .then(() => {
                // Create a customized copy of the blank tracker item
                // to act as the mass-update model.
                this.trackeritem = this.getBlankModel(_tracker);

                // Only include list-type EFs,
                // and make sure each has the "no change" special element
                this.extraFieldOptions = this.trackerService.massUpdate.getFieldOptions(_tracker);

                this.trackeritem.extraFields = this.trackerService.massUpdate.getDisplayFields(_tracker);
                this.extraFields = _.map(this.trackeritem.extraFields, (value, key) => value);
                this.trackeritem.rel.assignees = [];  // No change

                this.trackeritemOriginal = _.cloneDeep(this.trackeritem);
                this.isLoaded = true;

                this.updateAssignees = (assignees) => {
                    if (assignees) {
                        this.trackeritem.rel.assignees = assignees;
                    }
                };

                this.isLoaded = true;
            });
    }

    close() {
        this.onClose.emit();
    }

    cancel() {
        this.onClose.emit();
    }

    isTrackerItemUpdated() {
        return !this.utilsService.compareObjects(this.trackeritem, this.trackeritemOriginal);
    }

    isTagsUpdated() {
        const tags = _.map(this.tags, 'text');
        const tagsOriginal = _.map(this.tagsOriginal, 'text');
        return (tags.filter((item) => !tagsOriginal.includes(item)).length > 0 || tagsOriginal.filter((item) => !tags.includes(item)).length > 0);
    }
    
    tagsUpdated(tagsString: string) {
        if (!tagsString) {
            this.tags = [];
        } else {
            const tagsArray = tagsString.split(',');
            this.tags = tagsArray.map(tag => ({
                text: tag,
                bgcolor: this.uiService.color.stringToClassColor(tag)
            }));
        }
    }

    doMassUpdate() {
        this.trackeritem.priority = _.get(this.trackeritem, 'extraFields.priority.fieldData.id', '-1');
        
        let sprint = this.trackeritem?.extraFields?.sprint ?? null;
        if (_.get(sprint, 'fieldData.id', 0) > 0) {
            this.trackeritem.sprint = sprint;
        }

        // Don't submit the rel at all if no changes.
        if (_.get(this.trackeritem, 'rel.assignees.length', 0) == 0) {
            delete this.trackeritem.rel.assignees;
        }

        if (!this.isTrackerItemUpdated() && !this.isTagsUpdated()) {
            this.uiService.message.showInfo(this.translateService.translate("There are no changes to save"), "Mass Update");
            this.cancel();
            return;
        }

        this.updating = true;

        this.save(this.trackeritem)
            .then(() => {
                this.tagsOriginal = _.clone(this.tags);
                this.onUpdate.emit();
            }, () => {
                this.onClose.emit();
            });
    }

    showResults(response: any) {
        const succeeded = response.filter(item => item.succeeded);
        const failed = response.filter(item => !item.succeeded);

        switch (succeeded.length) {
            case 0: break;
            case 1:
                this.uiService.message.showSuccess(
                    this.translateService.translate('Successfully updated [#{{id}}]', { id: succeeded[0].data }),
                    this.translateService.translate('Mass Update')
                );
                break;

            default:
                this.uiService.message.showSuccess(
                    this.translateService.translate('Successfully updated {{c}} tracker items', { c: succeeded.length }),
                    this.translateService.translate('Mass Update')
                );
                break;
        }

        switch (failed.length) {
            case 0: break;
            case 1:
                this.uiService.message.showWarning(
                    this.translateService.translate('[#{{id}}] could not be updated.', { id: failed[0].data }) + ' ' +
                    this.translateService.translate('Edit directly for details on validation.'),
                    this.translateService.translate('Mass Update')
                );
                break;

            default:
                this.uiService.message.showWarning(
                    this.translateService.translate('{{c}} tracker items could not be updated.', { c: failed.length }) + ' ' +
                    this.translateService.translate('Edit directly for details on validation.'),
                    this.translateService.translate('Mass Update')
                );
                break;
        }
    }

    cleanFieldData(fieldData: any): any {
        return _.isObject(fieldData) ? 
            _.pick(fieldData, ['id', 'alias', 'value']) : fieldData;
    }

    cleanExtraFields(extraFields: any): any {
        const cleanFieldData = this.cleanFieldData;
        return _.reduce(
            extraFields,
            (carry, item, key) => {
                carry[key] = _.pick(item, ['id', 'alias', 'fieldData']);
                carry[key].fieldData = cleanFieldData(carry[key].fieldData);
                return carry;
            },
            {}
        );
    }

    save(trackeritem: any): Promise<void> {
        this.updating = true;

        // Clone the UI model, so we don't change the UI
        // during the save (i.e., removing EFs, etc.)
        const data = _.cloneDeep(trackeritem);
        
        // Only include EF values that are not "no change"
        // Also strip down the display-version EF to just 
        // what is needed for updating in the API.
        data.extraFields = this.cleanExtraFields(
            this.getChangedFields(trackeritem)
        );

        // Don't submit empty intrinsic fields
        if (!data.openDate) delete data.openDate;
        if (!data.closeDate) delete data.closeDate;
        if (!data.storyPoints) delete data.storyPoints;

        // Transform priority from -1 to not set
        // TODO: reconcile the value for no changes in the client
        if (data.priority == -1) {
            delete data.priority;
        }

        const sprintId = _.get(data, 'sprint.fieldData.id', -1);
        if (sprintId !== -1) {
            data.sprint = sprintId;
        } else {
            delete data.sprint;
        }

        const waitingForId = _.get(data, 'extraFields.waitingFor.fieldData.id', -1);
        if (waitingForId !== -1) {
            data.waitingFor = waitingForId;
        } else {
            delete data.extraFields.waitingFor;
        }

        const lockStatusId = _.get(data, 'extraFields.lockStatus.fieldData.value', -1);
        if (lockStatusId !== -1) {
            data.lockStatus = lockStatusId;
        } else {
            delete data.extraFields.lockStatus;
        }

        const assignees = _.get(data, 'rel.assignees', []);
        if (assignees.length == 0) {
            delete data.rel;
        }

        // Add tags to the API request
        const tags = _.map(_.get(this, 'tags', []), 'text');
        if (tags.length > 0) {
            data.tags = tags.join(',');
        }

        data.tis = _.map(this.trackeritems, (ti) => {
            return { id: ti.id, lastModifiedDate: ti.lastModifiedDate };
        });

        return new Promise<void>((resolve, reject) => {
            this.trackeritemRest.postMultiple(data)
                .then((response: any) => {
                    this.showResults(response);
                    
                    // Update original tags reference to prevent showing changes after saving
                    if (this.isTagsUpdated()) {
                        this.tagsOriginal = _.clone(this.tags);
                    }
                    
                    this.onUpdate.emit();
                    this.updating = false;
                    resolve();
                })
                .catch((failure) => {
                    this.uiService.message.showWarning(
                        this.translateService.translate("Sorry, could not update the specified TIs. An error occurred."),
                        this.translateService.translate('Mass Update')
                    );
                    this.updating = false;
                    reject();
                });
        });
    }

    private getChangedFields(trackeritem: any): any {
        return _.pickBy(_.get(trackeritem, 'extraFields', {}), (ef: any) => {
            let d: any = _.get(ef, 'fieldData', '');
            // Not supposed to allow multiple selections,
            // but just in case the UI ever changes,
            // this should still work.
            if (Array.isArray(d)) {
                if (d.length > 1) return true;
                d = d[0];
            }
            // Is this a fieldElement?
            if (_.isObject(d)) {
                return (_.get(d, 'id', 0) > -1);
            }
            // If not a fieldElement, is the scalar value set?
            return !_.isEmpty(d);
        });
    }

    private getBlankModel(tracker: any): any {
        const ti = this.trackeritemService.getBlankModel(tracker);

        delete ti.summary;
        ti.priority = -1;
        delete ti.details;
        delete ti.waiting_for;
        delete ti.tracker;
        ti.rel = {
            assignees: []
        };

        if (_.has(ti, 'sprint')) {
            const el: any = this.trackerService.massUpdate.noChangeEFElement;
            ti.sprint.fieldData.id = el.id;
            ti.sprint.fieldData.label = el.name;
            ti.sprint.fieldData.alias = el.alias;
        }

        return ti;
    }
}
